// React Components
import * as React from 'react';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';

// Material UI Components
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

// Audit Vault Components
import AccessDenied from '../components/common/AccessDenied';
import ServiceIsDown from '../components/common/ServiceIsDown';
import ProgressBar from '../components/common/ProgressBar';
import JobsList from '../components/jobs/JobsList';
import CustomerPageScaffold from "../components/common/CustomerPageScaffold";
import TenantRoleList from '../components/role/TenantRoleList';
import TenantDetails from '../components/tenant/TenantDetails';
import TenantHistory from '../components/tenant/TenantHistory';
import TenantSetupRequired from '../components/tenant/TenantSetupRequired';
import TenantPrivacyDetails from '../components/tenant/TenantPrivacyDetails';

// Audit Vault Utilities
import { getCompanyByCompanyId } from '../utilities/common-company';
import { verifyUserHasTenantRolePermissions } from '../utilities/common-user-utils';

// Audit Vault Constants
const theSectionTitle = "Your Tenant Dashboard";
const theSectionDesc = "Review and configure your Tenant settings here.  Review and edit your Audit Vault for M365's Tenant User Role security access assignments, and view your Tenant's Audit Vault Jobs.";
const isAdminPage = false;
import { ROLE_CODE_TENANTADMINISTRATOR } from '../constants/constants-roles';
import { LINK_TO_AUDIT_HISTORY_MENU_DEPLOYMENT_GUIDE, LINK_TO_OUR_MICROSOFT_SHAREPOINT_APP_STORE_PAGE, LINK_TO_OUR_WEBSITE_HELP_CENTER } from '../constants/constants-links';

function MyTenant(props) {

    const { user, openCompanyNag, setOpenCompanyNag } = props;
    const queryParams = new URLSearchParams(window.location.search);
    const navigate = useNavigate();

    // Component Constants
    const [company, setCompany] = useState("");
    const [loading, setLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);
    const [serviceIsDownError, setServiceIsDownError] = useState(false);

    const [value, setValue] = useState(0);  // Controls which tab is active.
    const [selectedTenant, setSelectedTenant] = useState(0);

    const [selectedTenantOption, setSelectedTenantOption] = useState('');
    const [selectedTenantChanged, setSelectedTenantChanged] = useState(false);
    const [selectedRole, setSelectedRole] = useState(1);

    const theBreadcrumbPath = [
        { name: 'Home', link: '/' },
        { name: 'Tenant', link: '/Tenant/MyTenant?tab=0' },
    ];

    // Component Functions

    function TabPanel(props) {
        const { children, value, index, ...other } = props;

        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && (
                    <Box sx={{ p: 3 }}>
                        <Typography>{children}</Typography>
                    </Box>
                )}
            </div>
        );
    }

    TabPanel.propTypes = {
        children: PropTypes.node,
        index: PropTypes.number.isRequired,
        value: PropTypes.number.isRequired,
    };

    function a11yProps(index) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    async function fetchData() {

        if (loading && user) {
            try {
                if (!verifyUserHasTenantRolePermissions(user, ROLE_CODE_TENANTADMINISTRATOR)) {
                    setAccessDenied(true);
                    return;
                }

                const response = await getCompanyByCompanyId(user.companyId, setServiceIsDownError);
                if (response) {

                    if (response != null) {
                        setCompany(response);

                        if (response.tenantList && response.tenantList.length > 0) {
                            setSelectedTenantOption(response.tenantList[0]);  // Set to the first item in the list.
                        }

                        setSelectedTenant(user.tenantId);
                        if (queryParams.get("roleId") && queryParams.get("roleId").length > 0) {
                            let roleNum = Number(queryParams.get("roleId"));
                            setSelectedRole(roleNum);
                            // open the role tab
                            setValue(1);
                        }
                    }
                }
            }
            catch (e) {
                console.log("ERROR: MyTenant.fetchData");
                console.log(e);
                setServiceIsDownError(true);
            }
            finally {
                setLoading(false);
            }
        }
    }

    useEffect(() => {
        fetchData();
        checkTabToDisplay();
    }, [window.location.search]);


    function checkTabToDisplay() {
        var tabVal = queryParams.get("tab");
        if (tabVal && tabVal.length > 0) {
            const requestedTab = parseInt(tabVal, 10);
            setValue(requestedTab === 4 && !(user.isSystemAdmin || user.isCompanyAdmin) ? 0 : requestedTab);
        }
    }

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleTenantOptionChange = (event) => {
        setSelectedTenantOption(event.target.value);
        setSelectedTenant(event.target.value.id);
    };

    const TenantSetupRequiredMsg = () => {
        return (
            <TenantSetupRequired />
        );
    };

    // Component UI
    if (loading) {
        return <ProgressBar message="Loading ..." loading={loading} />
    }

    if (serviceIsDownError) {
        return (<><ServiceIsDown></ServiceIsDown></>);
    }

    if (accessDenied) {
        return (<AccessDenied></AccessDenied>);
    }

    if (company && company.tenantList.length == 0) {
        return (<>
            <CustomerPageScaffold breadcrumbPaths={theBreadcrumbPath} sectionTitle={theSectionTitle} sectionDescription={theSectionDesc} isAdminPage={isAdminPage} company={company} openCompanyNag={openCompanyNag} setOpenCompanyNag={setOpenCompanyNag} showTenant={false} />
            {company.setupStageComplete >= 6 && <Button size="small" variant="contained" onClick={(event) => navigate(`/tenant-create`)}>Add tenant</Button>}
        </>)
    }

    if (!loading && company) {

        return (
            <>
                <CustomerPageScaffold breadcrumbPaths={theBreadcrumbPath} sectionTitle={theSectionTitle} sectionDescription={theSectionDesc} isAdminPage={isAdminPage} company={company} selectedTenant={selectedTenantOption} openCompanyNag={openCompanyNag} setOpenCompanyNag={setOpenCompanyNag} />

                <Box
                    sx={{
                        width: '100%',
                        minWidth: 0,
                    }}
                >
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            aria-label="tenant tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                        >
                            <Tab label="Tenant Summary" {...a11yProps(0)} />
                            <Tab label="Roles" {...a11yProps(1)} />
                            <Tab label="SharePoint App" {...a11yProps(2)} />
                            <Tab label="Jobs" {...a11yProps(3)} />
                            {(user.isSystemAdmin || user.isCompanyAdmin) && <Tab label="Privacy" {...a11yProps(4)} />}
                            <Tab label="Tenant History" {...a11yProps(5)} />
                        </Tabs>
                    </Box>

                    <TabPanel value={value} index={0}>
                        {company && <TenantDetails user={user} tenantId={selectedTenant} />}
                    </TabPanel>

                    <TabPanel value={value} index={1}>
                        {company.setupStageComplete > 3 && selectedTenantOption.firstAuditImportStage > 0 ? (
                            <Box sx={{ width: '100%' }}>
                                <Typography sx={{ mb: 2 }}>
                                    Below are the default security access role assignments for the selected Tenant.
                                    <br />
                                    Give users only the access they need by assigning the least-permissive role.
                                </Typography>

                                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                    <TenantRoleList
                                        user={user}
                                        tenantId={selectedTenant}
                                        roleNum={selectedRole}
                                        sourcePage="MyTenant"
                                        setSelectedTenantChanged={setSelectedTenantChanged}
                                        selectedTenantChanged={selectedTenantChanged}
                                    />
                                </Box>
                            </Box>
                        ) : (
                            TenantSetupRequiredMsg()
                        )}
                    </TabPanel>

                    <TabPanel value={value} index={2}>
                        {company.setupStageComplete > 3 ? (
                            <Box sx={{ width: '100%', maxWidth: 900 }}>
                                <Typography component="div">
                                    <p>
                                        <b>What is it:</b>
                                        <br />
                                        The Audit Vault for M365 - Audit History for SharePoint menu is an optional free
                                        app that can be added to your SharePoint tenant. The app allows you to enable an
                                        "Audit history" menu item and a command bar button directly in SharePoint. When
                                        used, the menu opens the Audit Vault for M365 "View SharePoint Item's Audit
                                        History" Report to display all the audit logs for the selected SharePoint Item.
                                    </p>

                                    <p>
                                        <b>Why you need it:</b>
                                        <br />
                                        Easily view Audit Log History directly from an Item in SharePoint Online:
                                    </p>

                                    <ul>
                                        <li>Expose and surface Item Audit History in SharePoint Online to empower your end users.</li>
                                        <li>Identify permissions issues.</li>
                                        <li>Track user activity on a particular document, folder or list item.</li>
                                    </ul>

                                    <p>
                                        <b>How to deploy it:</b>
                                        <br />
                                        Have your Microsoft Global or SharePoint Admin add the "Audit History for SharePoint"
                                        App to your Tenant from the Microsoft AppSource Store:
                                    </p>

                                    <ul>
                                        <li>
                                            <a
                                                href={LINK_TO_OUR_MICROSOFT_SHAREPOINT_APP_STORE_PAGE}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Microsoft AppSource: Audit History for SharePoint
                                            </a>
                                        </li>
                                    </ul>

                                    <Button
                                        type="button"
                                        href={LINK_TO_AUDIT_HISTORY_MENU_DEPLOYMENT_GUIDE}
                                        target="_blank"
                                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                                    >
                                        Learn More
                                    </Button>
                                </Typography>

                                <Card variant="outlined" sx={{ mt: 2 }}>
                                    <CardContent>
                                        <Typography
                                            component="div"
                                            variant="h5"
                                            sx={{ fontSize: 19, fontWeight: 800, pb: 0 }}
                                        >
                                            Current Audit History for SharePoint App Access:
                                        </Typography>

                                        <Box sx={{ color: 'green', mt: 1 }}>
                                            {selectedTenantOption && selectedTenantOption.useSaaPermsToAccessSpAudit === true ? (
                                                <>
                                                    <Typography component="div" variant="h6" sx={{ fontSize: 16, pb: 0 }}>
                                                        Uses Audit Vault for M365 Permissions
                                                    </Typography>
                                                    Requires users to have at least "Tenant Viewer" role access in Audit Vault for M365.
                                                </>
                                            ) : (
                                                <>
                                                    <Typography component="div" variant="h6" sx={{ fontSize: 16, pb: 0 }}>
                                                        Uses SharePoint Permissions
                                                    </Typography>
                                                    Requires users to have at least "View/Read" access to the Item in SharePoint.
                                                </>
                                            )}
                                        </Box>
                                    </CardContent>

                                    <CardActions
                                        sx={{
                                            justifyContent: { xs: 'stretch', sm: 'flex-start' },
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            type="button"
                                            onClick={() => navigate(`/tenant-update`)}
                                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                                        >
                                            Edit SharePoint App Permissions
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Box>
                        ) : (
                            TenantSetupRequiredMsg()
                        )}
                    </TabPanel>

                    <TabPanel value={value} index={3}>
                        {company && company.setupStageComplete > 3 && selectedTenant && selectedTenantOption.firstAuditImportStage > 0 ? (
                            <Box sx={{ width: '100%' }}>
                                <Typography component="div" sx={{ mb: 2 }}>
                                    Listed below are the Audit Jobs assigned to this tenant. Expand the section to view
                                    the job history over the past month.
                                </Typography>
                                <JobsList selectedTenant={selectedTenant} />
                            </Box>
                        ) : (
                            TenantSetupRequiredMsg()
                        )}
                    </TabPanel>

                    {(user.isSystemAdmin || user.isCompanyAdmin) && (
                        <TabPanel value={value} index={4}>
                            {company && company.setupStageComplete > 3 && user.tenantId && selectedTenantOption.firstAuditImportStage > 0 ? (
                                <TenantPrivacyDetails user={user} />
                            ) : (
                                TenantSetupRequiredMsg()
                            )}
                        </TabPanel>
                    )}

                    <TabPanel value={value} index={5}>
                        {company && company.setupStageComplete > 3 && selectedTenant && selectedTenantOption.firstAuditImportStage > 0 ? (
                            <TenantHistory user={user} company={company} tenantId={selectedTenant} />
                        ) : (
                            TenantSetupRequiredMsg()
                        )}
                    </TabPanel>
                </Box>
            </>
        );
    }
}
export default MyTenant;
